import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { getSuperAdminClinics, toggleClinicActive } from "../../apis/superadmin";
import { format } from "date-fns";

const { Search } = Input;

interface ClinicRow {
  id: number;
  name: string;
  email: string;
  phone: string;
  plan: string;
  isActive: boolean;
  usersCount: number;
  patientsCount: number;
  createdAt: string;
}

const PLAN_COLORS: Record<string, string> = {
  free: "default",
  essential: "blue",
  flexivisit: "green",
};

const ClinicList = () => {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState<ClinicRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState("");

  const fetchClinics = (page = 1, searchVal = search) => {
    setLoading(true);
    getSuperAdminClinics({ page, per_page: 20, search: searchVal || undefined })
      .then((res: any) => {
        setClinics(res.data.data);
        setPagination((p) => ({ ...p, current: res.data.currentPage, total: res.data.total }));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchClinics(1);
  }, []);

  const handleToggle = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    toggleClinicActive(id).then(() => fetchClinics(pagination.current));
  };

  const columns: ColumnsType<ClinicRow> = [
    {
      title: "Clinic",
      dataIndex: "name",
      render: (name, row) => (
        <div>
          <div className="font-medium text-gray-900">{name}</div>
          <div className="text-xs text-gray-500">{row.email}</div>
        </div>
      ),
    },
    { title: "Phone", dataIndex: "phone", width: 130 },
    {
      title: "Plan",
      dataIndex: "plan",
      width: 110,
      render: (plan) => (
        <Tag color={PLAN_COLORS[plan] ?? "default"}>{plan?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 90,
      render: (active) => (
        <Tag color={active ? "success" : "error"}>{active ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Users",
      dataIndex: "usersCount",
      width: 70,
      align: "center",
    },
    {
      title: "Patients",
      dataIndex: "patientsCount",
      width: 80,
      align: "center",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      width: 110,
      render: (v) => (v ? format(new Date(v), "dd MMM yyyy") : "—"),
    },
    {
      title: "",
      width: 100,
      render: (_, row) => (
        <Tooltip title={row.isActive ? "Deactivate" : "Activate"}>
          <Button
            size="small"
            danger={row.isActive}
            onClick={(e) => handleToggle(row.id, e)}
          >
            {row.isActive ? "Deactivate" : "Activate"}
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Clinics</h1>
          <p className="text-sm text-gray-500 mt-1">{pagination.total} clinics registered</p>
        </div>
        <Button type="primary" onClick={() => navigate("/superadmin/clinics/new")}>
          + New Clinic
        </Button>
      </div>

      <div className="mb-4">
        <Search
          placeholder="Search by name, email or phone..."
          allowClear
          style={{ maxWidth: 360 }}
          onSearch={(val) => {
            setSearch(val);
            fetchClinics(1, val);
          }}
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <Table
          rowKey="id"
          dataSource={clinics}
          columns={columns}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            onChange: (page) => fetchClinics(page),
          }}
          onRow={(row) => ({ onClick: () => navigate(`/superadmin/clinics/${row.id}`) })}
          rowClassName="cursor-pointer hover:bg-gray-50"
        />
      </div>
    </div>
  );
};

export default ClinicList;
